import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import { formatApiError } from '../lib/apiError';

const AuthContext = createContext(null);
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
axios.defaults.withCredentials = true;

const readStoredUser = () => {
  try {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

const cacheUser = (userData) => {
  if (userData) localStorage.setItem('user', JSON.stringify(userData));
  else localStorage.removeItem('user');
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(readStoredUser);
  const [loading, setLoading] = useState(true);

  const clearSession = useCallback(() => {
    setUser(null);
    cacheUser(null);
    localStorage.removeItem('token');
  }, []);

  const logout = useCallback(async () => {
    try { await axios.post(`${BACKEND_URL}/api/auth/logout`); } catch { /* cookie may already be gone */ }
    clearSession();
  }, [clearSession]);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${BACKEND_URL}/api/auth/me`)
      .then((res) => {
        if (cancelled) return;
        setUser(res.data);
        cacheUser(res.data);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err.response?.status === 401) clearSession();
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clearSession]);

  useEffect(() => {
    const id = axios.interceptors.response.use(
      (res) => res,
      (err) => {
        const url = err.config?.url || '';
        if (err.response?.status === 401 && url.startsWith(BACKEND_URL) && !url.includes('/api/auth/login')) {
          clearSession();
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, [clearSession]);

  const login = async (email, password) => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/auth/login`, { email, password });
      setUser(res.data.user);
      cacheUser(res.data.user);
      return { success: true };
    } catch (error) {
      return { success: false, error: formatApiError(error.response?.data?.detail, "Error al iniciar sesión") };
    }
  };

  const register = async (payload) => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/auth/register`, payload);
      setUser(res.data.user);
      cacheUser(res.data.user);
      return { success: true };
    } catch (error) {
      return { success: false, error: formatApiError(error.response?.data?.detail, "Error al registrarse") };
    }
  };

  const updateUser = (userData) => {
    setUser(userData);
    cacheUser(userData);
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, updateUser, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
